import {
    AttributeType,
    AttributeValue,
    PropsAttribute,
} from '../components/ui-element.type';
import { resolvePropertyDescriptor } from '../components/ui-element.helper';
import { nameConverter } from '../global/render-api';
import type { UIElement } from '../components/ui-element';
import type { GenericDecorator } from './decorator.type';
import {
    DefaultAttributeReflect,
    DefaultAttributeType,
} from '../components/ui-element.const';
import { isPlainObject } from '../global/helpers';
import { Mapped } from '../global/constants';

type AttributeDecoratorParam<T = HTMLElement> =
    | PropsAttribute<T>
    | AttributeType;

const isPropsAttribute = <T = HTMLElement>(
    params?: AttributeDecoratorParam<T>
): params is PropsAttribute<T> => isPlainObject(params);

const getInitialValueType = (value: AttributeValue): AttributeType =>
    typeof value as AttributeType;

// Overloads
export function Attribute(): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    type: AttributeType
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    type: AttributeType,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    params: PropsAttribute
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    params: PropsAttribute,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute<T>(
    params: PropsAttribute<T>
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute<T>(
    params: PropsAttribute<T>,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines an attribute for the component.
 * @param {AttributeDecoratorParam | Mapped} [typeOrParams] - Optional attribute type, configuration object, or Mapped enum value
 */
export function Attribute(
    typeOrParams?: AttributeDecoratorParam<HTMLElement> | Mapped
): GenericDecorator<void, ClassFieldDecoratorContext> {
    return (_: void, context: ClassFieldDecoratorContext) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<typeof UIElement>;
                const propertyName = String(context.name);
                const existingDescriptor = Object.getOwnPropertyDescriptor(
                    instance,
                    propertyName
                );
                const initialValue: AttributeValue =
                    existingDescriptor?.value as AttributeValue;
                let reflect: boolean = DefaultAttributeReflect;
                let adjustedType: AttributeType;

                // Determine if first param is actually Mapped enum
                const isMappedEnum = Object.values(Mapped).includes(
                    typeOrParams as Mapped
                );
                const resolvedParams = isMappedEnum
                    ? undefined
                    : (typeOrParams as AttributeDecoratorParam);

                if (isPropsAttribute(resolvedParams)) {
                    adjustedType = resolvedParams.type ?? DefaultAttributeType;
                    reflect = resolvedParams?.reflect !== false;
                } else {
                    adjustedType =
                        resolvedParams ??
                        getInitialValueType(initialValue) ??
                        DefaultAttributeType;
                }
                const effectiveDefault =
                    isPropsAttribute(resolvedParams) &&
                    resolvedParams.default !== undefined
                        ? resolvedParams.default
                        : initialValue;
                const attributeName = nameConverter.propToAttribute(
                    isPropsAttribute(resolvedParams)
                        ? resolvedParams.name || propertyName
                        : propertyName
                );
                const descriptor = resolvePropertyDescriptor(
                    attributeName,
                    adjustedType,
                    effectiveDefault,
                    reflect
                );
                Object.defineProperty(instance, propertyName, descriptor);
            });
        } else {
            throw new Error(
                '@Attribute decorator is only applied to the property'
            );
        }
    };
}
